// src/hooks/useSocket.js
import { COMMANDS } from '../../utils/commonProperties';
import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createTextSprite } from '../../utils/createTextSprite';
export function updateTextSprite(oldSprite, newText) {
  // allow empty string as valid newText, only reject null/undefined
  if (!oldSprite || newText === null || newText === undefined) {
    return;
  }
  const { drawText, texture, canvas } = oldSprite.userData || {};
  if (typeof drawText !== 'function' || !texture || !canvas) {
    return;
  }

  drawText(newText);
  texture.needsUpdate = true;

  // Yeni boyuta göre sprite ölçeğini güncelle
  oldSprite.scale.set(canvas.width / 10, canvas.height / 10, 1);
  oldSprite.userData.text = newText;

}

function disposeSprite(sprite) {
  if (!sprite) return;

  // Texture varsa dispose et
  if (sprite.material?.map) {
    sprite.material.map.dispose();
  }

  // Material varsa dispose et
  if (sprite.material) {
    sprite.material.dispose();
  }

  // Parent’tan kaldır
  if (sprite.parent) {
    sprite.parent.remove(sprite);
  }
}

const useComponentVisualUpdater = (scene, camera, renderer) => {
  /**
   * Bir text sprite'ın içeriğini günceller
   */


  const components = useSelector((state) => state.simpleSupply.components); 
  useEffect(() => {
    if (!scene || !components) return;

    // Helper: find object in scene with userData.id === comp.id
    function findObjectByUserDataId(root, id) {
      let found = null;
      root.traverse((obj) => {
        if (found) return;
        if (obj.userData && obj.userData.id === id) found = obj;
      });
      return found;
    }

    // Tüm componentler üzerinden geç
    components.forEach((comp) => {
      // Only operate on panel-like components. Skip supplies and other kinds.
      const isPanelLike = (
        comp.kind === 'panel' || comp.type === 'electricalPanel' ||
        comp.kind === 'meter_panel' || comp.kind === 'dist_panel' ||
        comp.metadata?.type === 'main_panel'
      );
      if (!isPanelLike) return;

      // Find the root group for this component in the scene by its id
      const compRoot = findObjectByUserDataId(scene, comp.id);
      if (!compRoot) return; // nothing to update in scene yet

      // Look for an explicit title group (created by createPanel/createMainPanel)
      const titleGroup = compRoot.getObjectByName('titleGroup') || compRoot.children.find(c => c.userData?.isTitle);
      if (!titleGroup) {
        // If there's no title group, do not create a separate small power sprite —
        // this avoids adding extra labels to supply/box or components that manage their own labels.
        return;
      }

      // Resolve installed power from multiple possible sources:
      // 1. Redux component electrical.installedPower
      // 2. component.metadata or details fields that may contain installedPower
      // 3. scene object userData fallbacks
      let installedPower = comp.electrical?.installedPower;
      if (installedPower === null || installedPower === undefined) {
        installedPower = comp.metadata?.installedPower || comp.details?.kuruluGuc || comp.installedPower || 0;
      }

      // Also check scene object userData as a last resort
      try {
        const scenePower = compRoot.userData?.installedPower || compRoot.userData?.electrical?.installedPower;
        if ((installedPower === null || installedPower === undefined || installedPower === 0) && scenePower) {
          installedPower = scenePower;
        }
      } catch (e) {}

      const newPowerText = `${installedPower || 0}W`;

      // If the titleGroup was created by createPanel/createMainPanel it should
      // include linkedSprites. Prefer updating those linked sprites directly
      // (index 0 = power, index 1 = name) instead of searching for power-like
      // children. This ensures the panel power updates even when no existing
      // small power candidate exists yet.
      const linkedSprites = titleGroup.userData?.linkedSprites || [];
      if (Array.isArray(linkedSprites) && linkedSprites.length >= 1) {
        // Prefer explicit role markers if present
        const powerLinked = linkedSprites.find(s => s && s.userData && s.userData.role === 'power') || linkedSprites[0];
        const nameLinked = linkedSprites.find(s => s && s.userData && s.userData.role === 'name') || linkedSprites[1] || linkedSprites[0];

        try { if (powerLinked) updateTextSprite(powerLinked, newPowerText); } catch (e) { console.error('Power update error:', e); }
        try { if (nameLinked) updateTextSprite(nameLinked, comp.name || ''); } catch (e) { console.error('Name update error:', e); }
        return;
      }

      // Try to find existing power-like sprites inside the title group
      const powerCandidates = titleGroup.children.filter(c => (c.userData && typeof c.userData.text === 'string' && c.userData.text.trim().endsWith('W')) || c.name === `panel_power_${comp.id}`);

      if (powerCandidates.length > 0) {
        // Deduplicate: if multiple found, choose the best candidate to keep.
        let keeper = null;

        // Prefer exact-name match
        keeper = powerCandidates.find(c => c.name === `panel_power_${comp.id}`) || null;

        // Prefer any linked sprite (they were created by createPanel)
        if (!keeper && Array.isArray(titleGroup.userData?.linkedSprites)) {
          keeper = powerCandidates.find(c => titleGroup.userData.linkedSprites.includes(c));
        }

        // Fallback: prefer the largest (most visible) sprite
        if (!keeper) {
          keeper = powerCandidates.reduce((best, cur) => {
            try {
              const curScale = (cur.scale && cur.scale.x) || 0;
              const bestScale = (best && best.scale && best.scale.x) || 0;
              return curScale > bestScale ? cur : best;
            } catch (e) {
              return best || cur;
            }
          }, null);
        }

        // Update keeper text if needed
        if (keeper && keeper.userData?.text !== newPowerText) {
          updateTextSprite(keeper, newPowerText);
        }

        // Dispose other duplicates
        powerCandidates.forEach((c) => {
          if (c !== keeper) {
            disposeSprite(c);
          }
        });

        return;
      }

      // As a final attempt: if one of the linkedSprites already contains the power text, update it
      const linked = titleGroup.userData?.linkedSprites || [];
      for (const s of linked) {
        if (s && s.userData && typeof s.userData.drawText === 'function' && typeof s.userData.text === 'string' && s.userData.text.endsWith('W')) {
          if (s.userData.text !== newPowerText) updateTextSprite(s, newPowerText);
          return;
        }
      }

      // No existing power label — create one inside the explicit title group
      const powerSprite = createTextSprite(newPowerText, {
        fontSize: 75,
        color: '#ffffff',
        backgroundColor: 'transparent',
        strokeColor: 'black',
        padding: 4
      });
      powerSprite.name = `panel_power_${comp.id}`;
      powerSprite.position.set(0, 0, 0.1);
      titleGroup.add(powerSprite);
    });
  }, [components, scene]);
};

export default useComponentVisualUpdater;